// Parental Consent Service - Handles COPPA compliance
import { ParentalConsent, ConsentStatus } from '../entities/ParentalConsent';

export interface ConsentRequestData {
  childEmail: string;
  childBirthdate: string;
  parentEmail: string;
  parentName?: string;
}

export class ParentalConsentService {
  // Generate unique verification token
  static generateToken(): string {
    return `consent_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  // Create consent request
  static async createConsentRequest(data: ConsentRequestData): Promise<ParentalConsent> {
    const token = this.generateToken();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // 7 days to respond

    const consentRequest: Omit<ParentalConsent, 'id' | 'created_at' | 'updated_at'> = {
      child_email: data.childEmail,
      child_birthdate: data.childBirthdate,
      parent_email: data.parentEmail,
      parent_name: data.parentName,
      verification_token: token,
      consent_status: 'pending',
      expires_at: expiresAt.toISOString(),
    };

    // In production, save to database here
    const saved = {
      ...consentRequest,
      id: Date.now(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as ParentalConsent;

    // Send email to parent
    await this.sendConsentEmail(saved);

    return saved;
  }

  // Send consent email to parent
  static async sendConsentEmail(consent: ParentalConsent): Promise<void> {
    const verificationUrl = `${window.location.origin}/parental-consent?token=${consent.verification_token}`;
    
    const emailTemplate = this.getEmailTemplate(consent, verificationUrl);

    // In production, use actual email service (SendGrid, AWS SES, etc.)
    console.log('📧 ========== PARENTAL CONSENT EMAIL ==========');
    console.log('To:', consent.parent_email);
    console.log('Subject:', emailTemplate.subject);
    console.log('Verification URL:', verificationUrl);
    console.log('==============================================');

    // For DEVELOPMENT ONLY: Show alert with clickable link
    const alertMessage = 
      `📧 PARENTAL CONSENT EMAIL (DEMO MODE)\n\n` +
      `✅ Email would be sent to: ${consent.parent_email}\n\n` +
      `During development, no actual email is sent.\n` +
      `Copy this verification link to test:\n\n` +
      `${verificationUrl}\n\n` +
      `In production, this will be sent via SendGrid/AWS SES.`;
    
    alert(alertMessage);
    
    // Log to console so you can copy the link
    console.log('🔗 COPY THIS LINK TO TEST:', verificationUrl);

    // Store email content locally for demo
    localStorage.setItem(`consent_email_${consent.verification_token}`, JSON.stringify({
      to: consent.parent_email,
      subject: emailTemplate.subject,
      html: emailTemplate.html,
      url: verificationUrl,
      sentAt: new Date().toISOString()
    }));
  }

  // Email template
  static getEmailTemplate(consent: ParentalConsent, verificationUrl: string) {
    const childAge = this.calculateAge(consent.child_birthdate);
    
    return {
      subject: '🔐 Parental Consent Required - WHIPT Academic Platform',
      html: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center; }
            .content { background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; }
            .warning { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 20px 0; border-radius: 5px; }
            .info-box { background: white; border: 2px solid #e5e7eb; padding: 20px; margin: 20px 0; border-radius: 8px; }
            .button { display: inline-block; background: #10b981; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; margin: 20px 0; }
            .footer { text-align: center; color: #6b7280; font-size: 12px; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; }
            ul { padding-left: 20px; }
            li { margin: 8px 0; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🔐 Parental Consent Required</h1>
            <p>WHIPT Academic Writing Platform</p>
          </div>
          
          <div class="content">
            <p>Dear Parent/Guardian${consent.parent_name ? ` (${consent.parent_name})` : ''},</p>
            
            <p>A child under 13 years old has attempted to create an account on the WHIPT academic platform using your email address as their parent/guardian contact.</p>
            
            <div class="warning">
              <strong>⚠️ IMPORTANT:</strong> Federal law (COPPA) requires us to obtain verifiable parental consent before allowing children under 13 to use our platform.
            </div>
            
            <div class="info-box">
              <h3>📋 Account Details:</h3>
              <ul>
                <li><strong>Child's Email:</strong> ${consent.child_email}</li>
                <li><strong>Child's Age:</strong> ${childAge} years old</li>
                <li><strong>Date Requested:</strong> ${new Date(consent.created_at).toLocaleString()}</li>
              </ul>
            </div>
            
            <div class="info-box">
              <h3>🎓 What is WHIPT?</h3>
              <p>WHIPT is an educational writing assistant designed for students. It provides:</p>
              <ul>
                <li>Academic writing tools and templates</li>
                <li>Citation management</li>
                <li>Study organization features</li>
                <li>Collaboration with classmates (age-restricted)</li>
              </ul>
            </div>
            
            <div class="info-box">
              <h3>🔒 Privacy & Safety for Children Under 13:</h3>
              <ul>
                <li><strong>No Social Features:</strong> Messaging and social features are disabled</li>
                <li><strong>No Public Profile:</strong> Child's information is private</li>
                <li><strong>Limited Data Collection:</strong> Only essential information for the service</li>
                <li><strong>No Third-Party Sharing:</strong> Child's data is never sold or shared</li>
                <li><strong>Parent Access:</strong> You can review and delete data at any time</li>
                <li><strong>FERPA Compliant:</strong> Meets educational privacy standards</li>
              </ul>
            </div>
            
            <div class="info-box">
              <h3>📊 Data We Collect:</h3>
              <ul>
                <li>Email address (for login)</li>
                <li>Age/birthdate (for age verification)</li>
                <li>Written documents and assignments</li>
                <li>Usage data (feature usage, login times)</li>
              </ul>
              <p><strong>We DO NOT collect:</strong> Location data, financial information, or sensitive personal data.</p>
            </div>
            
            <h3 style="margin-top: 30px;">✅ To Grant Consent:</h3>
            <p>Click the button below to review and approve your child's account. This link expires in 7 days.</p>
            
            <a href="${verificationUrl}" class="button">
              ✅ Review & Grant Consent
            </a>
            
            <p style="font-size: 14px; color: #6b7280;">
              Or copy this link to your browser:<br>
              <code style="background: #e5e7eb; padding: 5px; display: block; margin-top: 5px; word-break: break-all;">${verificationUrl}</code>
            </p>
            
            <div class="warning" style="margin-top: 30px;">
              <strong>🚫 To Deny Access:</strong><br>
              Simply ignore this email. The account will remain inactive and will be automatically deleted after 30 days.
            </div>
            
            <p style="margin-top: 30px; font-size: 14px;">
              <strong>Questions?</strong> Contact us at <a href="mailto:support@whipt.app">support@whipt.app</a> or call (555) 123-4567.
            </p>
            
            <p style="margin-top: 20px; font-size: 14px;">
              <strong>Legal:</strong> View our complete 
              <a href="${window.location.origin}/privacy">Privacy Policy</a> and 
              <a href="${window.location.origin}/terms">Terms of Service</a>.
            </p>
          </div>
          
          <div class="footer">
            <p>
              <strong>WHIPT Academic Writing Platform</strong><br>
              COPPA & FERPA Compliant Educational Tool<br>
              This email was sent because someone used your email address to register a child account.<br>
              If you did not initiate this, please ignore this email.
            </p>
            <p style="margin-top: 10px;">
              © ${new Date().getFullYear()} WHIPT. All rights reserved.
            </p>
          </div>
        </body>
        </html>
      `,
      text: `
Parental Consent Required - WHIPT Academic Platform

Dear Parent/Guardian,

A child under 13 years old has attempted to create an account on WHIPT using your email address.

Federal law (COPPA) requires parental consent before children under 13 can use our platform.

ACCOUNT DETAILS:
- Child's Email: ${consent.child_email}
- Child's Age: ${childAge} years old
- Date: ${new Date(consent.created_at).toLocaleString()}

TO GRANT CONSENT:
Visit this link (expires in 7 days):
${verificationUrl}

TO DENY ACCESS:
Simply ignore this email. The account will remain inactive.

Questions? Contact support@whipt.app or (555) 123-4567

View our Privacy Policy: ${window.location.origin}/privacy
View our Terms of Service: ${window.location.origin}/terms

© ${new Date().getFullYear()} WHIPT - COPPA & FERPA Compliant
      `
    };
  }

  // Calculate age from birthdate
  static calculateAge(birthdate: string): number {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  }

  // Verify consent token
  static async verifyConsent(token: string, ipAddress?: string, userAgent?: string): Promise<ParentalConsent | null> {
    // In production, fetch from database
    const stored = localStorage.getItem(`consent_${token}`);
    if (!stored) return null;

    const consent: ParentalConsent = JSON.parse(stored);

    // Check if expired
    if (new Date(consent.expires_at) < new Date()) {
      consent.consent_status = 'expired';
      localStorage.setItem(`consent_${token}`, JSON.stringify(consent));
      return consent;
    }

    return consent;
  }

  // Approve consent
  static async approveConsent(token: string, ipAddress?: string, userAgent?: string): Promise<boolean> {
    const consent = await this.verifyConsent(token);
    if (!consent || consent.consent_status !== 'pending') {
      return false;
    }

    consent.consent_status = 'approved';
    consent.consent_given_at = new Date().toISOString();
    consent.ip_address = ipAddress;
    consent.user_agent = userAgent;
    consent.updated_at = new Date().toISOString();

    // In production, update database
    localStorage.setItem(`consent_${token}`, JSON.stringify(consent));
    
    // CRITICAL: Update age verification to activate account
    // First try to get user ID from child email
    console.log('🔍 Looking up user for activation:', consent.child_email);
    
    try {
      const { turso } = await import('@/database/client');
      
      // Find user by email
      const userResult = await turso.execute({
        sql: 'SELECT id FROM users WHERE email = ? LIMIT 1',
        args: [consent.child_email]
      });
      
      console.log('📋 User lookup result:', userResult.rows);
      
      if (userResult.rows && userResult.rows.length > 0) {
        const userId = userResult.rows[0].id as string;
        console.log('✅ Found user ID:', userId);
        
        // Get current age verification from database
        const ageDataResult = await turso.execute({
          sql: "SELECT data_value FROM user_data WHERE user_id = ? AND data_type = 'age_verification' LIMIT 1",
          args: [userId]
        });
        
        console.log('📋 Age verification lookup result:', ageDataResult.rows);
        
        if (ageDataResult.rows && ageDataResult.rows.length > 0) {
          const restrictions = JSON.parse(ageDataResult.rows[0].data_value as string);
          console.log('📄 Current restrictions:', restrictions);
          
          // ACTIVATE THE ACCOUNT
          restrictions.accountActive = true;
          restrictions.parentalConsentApproved = true;
          restrictions.parentalConsentStatus = 'approved';
          restrictions.parentalConsentDate = new Date().toISOString();
          
          console.log('✨ Updated restrictions:', restrictions);
          
          // Update database with activated account
          await turso.execute({
            sql: `
              UPDATE user_data 
              SET data_value = ?, updated_at = datetime('now')
              WHERE user_id = ? AND data_type = 'age_verification'
            `,
            args: [JSON.stringify(restrictions), userId]
          });
          
          console.log('✅ Account activated in database for user:', userId);
          
          // Also update localStorage if it exists (for current session)
          localStorage.setItem('whipt_age_verification', JSON.stringify(restrictions));
        } else {
          console.warn('⚠️ No age verification found in database for user:', userId);
        }
      } else {
        console.warn('⚠️ No user found with email:', consent.child_email);
      }
    } catch (dbError) {
      console.error('❌ Failed to activate account in database:', dbError);
      // Fallback to localStorage only
      const ageVerification = localStorage.getItem('whipt_age_verification');
      if (ageVerification) {
        const restrictions = JSON.parse(ageVerification);
        restrictions.accountActive = true;
        restrictions.parentalConsentApproved = true;
        restrictions.parentalConsentStatus = 'approved';
        restrictions.parentalConsentDate = new Date().toISOString();
        localStorage.setItem('whipt_age_verification', JSON.stringify(restrictions));
        console.log('✅ Activated in localStorage as fallback');
      }
    }

    return true;
  }

  // Deny consent
  static async denyConsent(token: string): Promise<boolean> {
    const consent = await this.verifyConsent(token);
    if (!consent || consent.consent_status !== 'pending') {
      return false;
    }

    consent.consent_status = 'denied';
    consent.updated_at = new Date().toISOString();

    // In production, update database
    localStorage.setItem(`consent_${token}`, JSON.stringify(consent));

    return true;
  }

  // Check if user has pending consent
  static hasPendingConsent(childEmail: string): boolean {
    // In production, check database
    // For now, check localStorage
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith('consent_consent_')) {
        const consent: ParentalConsent = JSON.parse(localStorage.getItem(key) || '{}');
        if (consent.child_email === childEmail && consent.consent_status === 'pending') {
          return true;
        }
      }
    }
    return false;
  }
}
